import {Editor, Element, Node, Text, Transforms} from "slate";
import {HistoryEditor} from "slate-history";
import {ReactEditor} from "slate-react";

import {setBlockType} from "./block-helpers.ts";

/**
 * Checks whether the currently focused element is the Slate editor.
 */
export const isEditorFocused = () =>
    document.activeElement?.matches("[data-slate-editor]") || false;

const TITLE_NODES = ["heading-one", "paragraph"];

/**
 * Gets a document title for the current editor. This is derived from the first
 * heading-one found within the document (assumed to be the title). If none is
 * found, we return a reasonable default.
 */
export const getDocumentTitle = (editor: Editor): string => {
    for (const node of editor.children) {
        if (Element.isElement(node) && TITLE_NODES.includes(node.type)) {
            const title = Node.string(node);

            return title.trim() === "" ? "Khanmigo document" : title;
        }
    }

    return "Khanmigo document";
};

export const getAndSelectParentBlock = (editor: Editor) => {
    if (!editor.selection) {
        return;
    }

    // Find the parent node of the current selection or cursor
    const parentPath = Editor.parent(editor, editor.selection.anchor.path)[1];

    const start = Editor.start(editor, parentPath);
    const end = Editor.end(editor, parentPath);
    const newSelection = {anchor: start, focus: end};
    Transforms.select(editor, newSelection);
};

export const unsetDomSelectionFromEditor = (editor: Editor | null) => {
    if (!editor) {
        return;
    }

    const doc = ReactEditor.findDocumentOrShadowRoot(editor);
    if (!(doc instanceof Document)) {
        return;
    }

    const domSelection = doc.getSelection();
    if (!domSelection) {
        return;
    }

    if (ReactEditor.hasTarget(editor, domSelection.focusNode)) {
        domSelection.removeAllRanges();
    }
};

/**
 * Updates a meaningless property on all text nodes in the editor to force them
 * each to re-render. This is useful for ensuring decorations get updated. By
 * default, Slate does not recalculate a node's decorations unless it is edited
 * or selected, but we sometimes want to recalculate them all at once (for
 * example, when the editor gains focus, we want to clear any decorations from
 * Khanmigo's old selection).
 */
export const rerenderTextNodes = (editor: Editor | null) => {
    if (!editor) {
        return;
    }

    // Since this isn't a user-initiated or visible transform, don't save
    // it to the history stack so that clicking "Undo" or "Redo" won't
    // undo/redo these invisible changes.
    HistoryEditor.withoutSaving(editor, () => {
        Transforms.setNodes(
            editor,
            {_: Symbol()},
            {at: [], match: Text.isText},
        );
    });
};

/**
 * Deletes all the content from the given editor.
 */
export const clearEditor = (editor: Editor): void => {
    Transforms.delete(editor, {
        at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
        },
    });

    // Reset formatting to the default block type:
    setBlockType(editor, "paragraph");
};
